import { CloseCircleOutlined, FileExcelOutlined, FileImageOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined, PaperClipOutlined } from "@ant-design/icons";
import { message as antMessage, Button, Form, Input, Popover, Upload } from "antd";
import React, { FC, useState } from "react";

//!REDUX
import { useDispatch, useSelector } from "react-redux";
import {
    useGetAllRoomsQuery,
    useSendMessageMutation,
} from "store/services/chat";

//!STYLE
import s from "./style/message.module.css";

//!IMAGES
import { UserRoles } from "common/enums";
import SendIcon from "../../../../assets/images/app/ai_send_icon.svg";

import { skipToken } from "@reduxjs/toolkit/query";

type Props = {
    conversation: any;
    setConversation: React.Dispatch<any>;
    setIsTyping: React.Dispatch<any>;
    isBlocked: boolean;
    setIsAttachment: React.Dispatch<boolean>;
};

type AttachmentType = {
    url: string;
    publicId: string;
    fileName: string;
    mimeType: string;
    size: number;
    width?: number;
    height?: number;
};

const Message: FC<Props> = ({ conversation, setConversation, setIsTyping, isBlocked, setIsAttachment }) => {
    //!HOOKS
    const dispatch = useDispatch();
    const {
        talkId,
        idReceiver,
        socket,
        idSender,
        conversation: conversationStore,
    } = useSelector((state) => state.appChat);
    const { user } = useSelector((state) => state.auth);
    const [sendMessage, { data, isLoading, isError }] = useSendMessageMutation();

    const { refetch } = useGetAllRoomsQuery(
        user?._id ?? skipToken
    );

    const [lastMessage, setLastMessage] = useState([]);
    const [form] = Form.useForm();

    // Attachment states
    const [attachment, setAttachment] = useState<AttachmentType | null>(null);
    const [messageType, setMessageType] = useState<'text' | 'image' | 'file'>('text');
    const [uploadLoading, setUploadLoading] = useState(false);
    const [filePreview, setFilePreview] = useState<{ name: string; type: string; preview?: string } | null>(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
    const [isXsScreen, setIsXsScreen] = useState(window.innerWidth > 767 && window.innerWidth <= 1151);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth > 1151 && window.innerWidth <= 1321);
    const [isMediumScreen, setIsMediumScreen] = useState(window.innerWidth > 1321 && window.innerWidth <= 1606);
    const [isNormalScreen, setIsNormalScreen] = useState(window.innerWidth > 1606 && window.innerWidth <= 1799);

    const handleResize = () => {
        setIsMobile(window.innerWidth <= 767);
        setIsXsScreen(window.innerWidth > 767 && window.innerWidth <= 1151);
        setIsSmallScreen(window.innerWidth > 1151 && window.innerWidth <= 1321);
        setIsMediumScreen(window.innerWidth > 1321 && window.innerWidth <= 1606);
        setIsNormalScreen(window.innerWidth > 1606 && window.innerWidth <= 1799);
    };
    window.addEventListener('resize', handleResize);

    const isParent = user?.roles?.includes(UserRoles.PARENT);
    const isOrganism = user?.roles?.includes(UserRoles.ORGANISME);

    // Format file size
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    // Get file icon based on mime type
    const getFileIcon = (mimeType: string) => {
        if (mimeType.includes('pdf')) {
            return <FilePdfOutlined style={{ fontSize: '20px', color: '#ff4d4f' }} />;
        } else if (mimeType.includes('word') || mimeType.includes('document')) {
            return <FileWordOutlined style={{ fontSize: '20px', color: '#1890ff' }} />;
        } else if (mimeType.includes('excel') || mimeType.includes('sheet')) {
            return <FileExcelOutlined style={{ fontSize: '20px', color: '#52c41a' }} />;
        }
        return <FileOutlined style={{ fontSize: '20px', color: '#595959' }} />;
    };

    // Upload file to Cloudinary
    const uploadFile = async (file: File): Promise<AttachmentType | null> => {
        try {
            setUploadLoading(true);

            const formData = new FormData();
            formData.append('file', file);
            formData.append('upload_preset', 'chat_files_unsigned');

            const cloudinaryUrl = 'https://api.cloudinary.com/v1_1/dcfvrpurc/auto/upload';

            const response = await fetch(cloudinaryUrl, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Cloudinary error:', errorData);
                throw new Error(errorData.error?.message || 'Upload failed');
            }

            const data = await response.json();

            let dimensions: { width?: number; height?: number } = {};
            if (file.type.startsWith('image/') && data.width && data.height) {
                dimensions = {
                    width: data.width,
                    height: data.height,
                };
            }

            return {
                url: data.secure_url,
                publicId: data.public_id,
                fileName: file.name,
                mimeType: file.type,
                size: file.size,
                ...dimensions,
            };
        } catch (error) {
            console.error('Upload error:', error);
            antMessage.error('Échec du téléchargement du fichier');
            return null;
        } finally {
            setUploadLoading(false);
        }
    };

    const handleFileSelect = async (file: File, type: 'image' | 'file') => {
        // Validate file type
        const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
        const allowedFileTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ];

        if (type === 'image' && !allowedImageTypes.includes(file.type)) {
            antMessage.error('Type d\'image non supporté. Veuillez choisir JPG, PNG, GIF, WEBP ou SVG.');
            return false;
        }

        if (type === 'file' && !allowedFileTypes.includes(file.type)) {
            antMessage.error('Type de fichier non supporté. Veuillez choisir PDF, Word ou Excel.');
            return false;
        }

        // Validate file size (e.g., 10MB max)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            antMessage.error('Le fichier est trop volumineux. Taille maximale: 10MB');
            return false;
        }

        // Create preview URL for images
        let previewUrl: string | undefined;
        if (type === 'image') {
            previewUrl = URL.createObjectURL(file);
        }

        // Upload file
        const uploadedAttachment = await uploadFile(file);

        if (uploadedAttachment) {
            setAttachment(uploadedAttachment);
            setMessageType(type);
            setFilePreview({
                name: file.name,
                type: file.type,
                preview: previewUrl,
            });
            antMessage.success('Fichier téléchargé avec succès');
            setIsAttachment(true);
        }

        return false; // Prevent default upload behavior
    };

    const removeAttachment = () => {
        // Revoke object URL to free memory
        if (filePreview?.preview) {
            URL.revokeObjectURL(filePreview.preview);
        }
        setAttachment(null);
        setMessageType('text');
        setFilePreview(null);
        setIsAttachment(false);
    };

    const onSendMessage = async (values?: any) => {
        const hasMessage = values?.msg && values.msg.trim() !== '';
        const hasAttachment = attachment !== null;

        // Must have either message or attachment
        if (!hasMessage && !hasAttachment) {
            antMessage.warning('Veuillez saisir un message ou joindre un fichier');
            return;
        }

        // Validate that we have a valid talkId
        if (!talkId) {
            console.error("Cannot send message: Missing talkId");
            return;
        }

        // Determine if this is a group chat
        const isGroupChat = Boolean(conversationStore?.receiverIdC);

        // Determine message type
        let finalMessageType: 'text' | 'image' | 'file' = 'text';
        if (hasAttachment) {
            finalMessageType = messageType;
        }

        // Build message body
        const bodySendMessage: any = {
            talkId: talkId,
            sender: user?._id,
            receiver: isGroupChat
                ? conversationStore?.receiverIdC?._id
                : (user?._id === idSender ? idReceiver : idSender),
            message: hasMessage ? values.msg.trim() : '',
            flag: isGroupChat ? "GROUP_FLAG" : "SINGLE_FLAG",
            instaprof: conversationStore?.instaProf ? "find" : "",
            messageType: finalMessageType,
        };

        // Add attachment if present
        if (hasAttachment && attachment) {
            bodySendMessage.attachment = attachment;
        }

        try {
            socket?.connect();
            console.log(bodySendMessage);
            await socket.emit("sendMessage", bodySendMessage);

            // Reset form and attachments
            form.resetFields();
            removeAttachment();
        } catch (error) {
            console.error("[ERROR] Failed to send message:", error);
            antMessage.error('Échec de l\'envoi du message');
        }
    };

    const handleTyping = async () => {
        socket?.connect();
        await socket.emit("isTyping", talkId);
    };

    const attachmentContent = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '150px' }}>
            <Upload
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                beforeUpload={(file) => handleFileSelect(file, 'image')}
                showUploadList={false}
                maxCount={1}
            >
                <Button
                    type="text"
                    icon={<FileImageOutlined />}
                    style={{ width: '100%', textAlign: 'left' }}
                >
                    Image
                </Button>
            </Upload>

            <Upload
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                beforeUpload={(file) => handleFileSelect(file, 'file')}
                showUploadList={false}
                maxCount={1}
            >
                <Button
                    type="text"
                    icon={<FileOutlined />}
                    style={{ width: '100%', textAlign: 'left' }}
                >
                    Fichier
                </Button>
            </Upload>
        </div>
    );

    return (
        <Form onFinish={onSendMessage} form={form}>
            {!isBlocked && (
                <div className={s.inputTextarea}>
                    {/* Attachment Button - Only show when no file is attached */}
                    {!filePreview && (
                        <Popover
                            content={attachmentContent}
                            trigger="click"
                            placement="topLeft"
                        >
                            <Button
                                type="text"
                                icon={<PaperClipOutlined />}
                                loading={uploadLoading}
                                className={s.attachmentButton}
                            />
                        </Popover>
                    )}

                    {/* Text Input with File Preview Inside */}
                    <div className={s.inputWrapper}>
                        {/* File Preview Inside Input Area */}
                        {filePreview && (
                            <div className={s.filePreviewContainer}>
                                {messageType === 'image' && filePreview.preview ? (
                                    // Image Preview with Thumbnail
                                    <div className={s.imagePreview}>
                                        <img
                                            src={filePreview.preview}
                                            alt="Preview"
                                            className={s.previewImage}
                                        />
                                        <div className={s.previewInfo}>
                                            <div className={s.previewFileName}>{filePreview.name}</div>
                                            {attachment && (
                                                <div className={s.previewFileSize}>
                                                    {formatFileSize(attachment.size)}
                                                </div>
                                            )}
                                        </div>
                                        <CloseCircleOutlined
                                            onClick={removeAttachment}
                                            className={s.removeButton}
                                        />
                                    </div>
                                ) : (
                                    // File Preview (PDF, Word, Excel)
                                    <div className={s.filePreview}>
                                        <div className={s.filePreviewIcon}>
                                            {getFileIcon(filePreview.type)}
                                        </div>
                                        <div className={s.previewInfo}>
                                            <div className={s.previewFileName}>{filePreview.name}</div>
                                            {attachment && (
                                                <div className={s.previewFileSize}>
                                                    {formatFileSize(attachment.size)}
                                                </div>
                                            )}
                                        </div>
                                        <CloseCircleOutlined
                                            onClick={removeAttachment}
                                            className={s.removeButton}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        <Form.Item
                            name={"msg"}
                            style={{ marginTop: 0, padding: 0, width: "100%", marginBottom: 0 }}
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if ((value && value.trim() !== '') || attachment) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject();
                                    },
                                    message: ""
                                },
                            ]}
                        >
                            <Input.TextArea
                                className={s.messageInput}
                                onChange={handleTyping}
                                placeholder={(isParent || isOrganism) ? "Écrivez votre message ici..." : "Écris ton message ici..."}
                                rows={(isMobile || isXsScreen || isSmallScreen) ? 1 : 2}
                                bordered={false}
                                autoSize={{ minRows: (isMobile || isXsScreen || isSmallScreen) ? 1 : 2, maxRows: 4 }}
                            />
                        </Form.Item>
                    </div>

                    {/* Send Button */}
                    <Button
                        type="link"
                        htmlType="submit"
                        loading={uploadLoading}
                        className={s.sendButton}
                    >
                        <img
                            src={SendIcon}
                            style={{
                                position: "relative",
                                top: "50%",
                                right: "10px",
                                transform: "translateY(-50%)",
                                zIndex: 1,
                            }}
                            height={(isMobile || isXsScreen || isSmallScreen) ? 30 : 40}
                            alt="Send"
                        />
                    </Button>
                </div>
            )}
            {isBlocked && <div className={s.BlockedmessageBox}>Cette conversation est bloquée</div>}
        </Form>
    );
};

export default Message;